/**
 * Use Dev Intake Hook
 * 
 * Wires the dev intake utilities into the React component tree.
 * Provides access to the routing context for live lead testing.
 */

import { useEffect, useMemo } from 'react';
import { setDevRoutingContext, initDevIntake } from '@/lib/leads/devIntake';
import type { RoutingContext } from '@/lib/routingOrchestrator';
import { useMockAgents } from '@/contexts/MockAgentsContext';
import { useAllocationWeights } from '@/contexts/AllocationWeightsContext';
import { usePricingRules } from '@/contexts/PricingRulesContext';
import { useSystemControl } from '@/contexts/SystemControlContext';
import { useContracts } from '@/contexts/ContractsContext';

/**
 * Hook to set up dev intake with the current routing context.
 * Call this at the app root or admin layout level.
 */
export function useDevIntakeContext() {
  const { agents } = useMockAgents();
  const { state: weightsState, actions: weightsActions } = useAllocationWeights();
  const { actions: pricingActions } = usePricingRules();
  const { actions: systemActions } = useSystemControl();
  const { state: contractsState, actions: contractActions } = useContracts();
  
  // Build routing context
  const routingContext: RoutingContext = useMemo(() => {
    // Build contract pressures map
    const contractPressures = new Map();
    for (const agent of agents) {
      const pressure = contractActions.getAgentPressure(agent.id);
      if (pressure) {
        contractPressures.set(agent.id, pressure);
      }
    }
    
    const weights = weightsActions.getWeights();
    
    return {
      agents,
      weights,
      getPricingRule: (leadTypeId: string, state: string) => {
        const rule = pricingActions.getPricingRuleForLead(leadTypeId, state);
        if (rule) {
          return { basePrice: rule.basePrice, active: rule.active };
        }
        return null;
      },
      checkIntake: (vertical: 'life_insurance' | 'mortgage') => {
        return systemActions.getIntakeRejectionReason(vertical);
      },
      contractPressures,
      targetPoolSize: 8,
      usePoolSelection: agents.length > 10,
    };
  }, [agents, weightsState, weightsActions, pricingActions, systemActions, contractActions, contractsState]);
  
  // Set dev context whenever it changes
  useEffect(() => {
    if (agents.length > 0) {
      setDevRoutingContext(routingContext);
    }
    
    return () => {
      setDevRoutingContext(null);
    };
  }, [routingContext, agents.length]);
  
  // Initialize dev intake on mount
  useEffect(() => {
    initDevIntake();
  }, []);
  
  return routingContext;
}

/**
 * Hook to intake a single lead with the current context.
 */
export function useIntakeLead() {
  const routingContext = useDevIntakeContext();
  
  return useMemo(() => ({
    routingContext,
    isReady: routingContext.agents.length > 0,
  }), [routingContext]);
}
